import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import { LIST_ACTION_EDIT_BUTTON_SX } from '../../component/listActionIconStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatDate } from '../../utils/dateDisplay'
import {
    applyAppCompanyDraft,
    clearAppCompanyDraft,
    createAppCompanyDraftPayload,
    createEditAppCompanyDraftKey,
    readAppCompanyDraft,
    saveAppCompanyDraft,
} from './appCompanyDraftUtils'

const DEFAULT_RANKING_TABS = [
    { tabKey: 'event', label: '이벤트전', sortOrder: 1, exposureMode: 'ON' },
    { tabKey: 'department', label: '부서별 대항전', sortOrder: 2, exposureMode: 'OFF' },
    { tabKey: 'country', label: '국가대항전', sortOrder: 3, exposureMode: 'OFF' },
]
const DEFAULT_BENEFIT_MISSIONS = [
    { benefitKey: 'company_attendance', label: '기업 전용 출석체크', exposureMode: 'ON', startTime: null, endTime: null },
    { benefitKey: 'daily_goal_step_point', label: '매일 목표 걸음수 달성하고 포인트 받기', exposureMode: 'ON', startTime: null, endTime: null },
    { benefitKey: 'mission_day_bonus_point', label: '미션 데이 참여하고 보너스 포인트 받기', exposureMode: 'ON', startTime: null, endTime: null },
]
const COMPANY_DOMAIN_PAGE_SIZE = 100

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    companyCode: '',
    companyName: '',
    companyNameEn: '',
    email: '',
    isSignUpVisible: 1,
    planType: 1,
    limitUsers: 0,
    planStartTime: null,
    planEndTime: null,
    planFirstTime: null,
    memo: '',
    company_admin_info: [],
})

const RANKING_TAB_LABELS = DEFAULT_RANKING_TABS.reduce((result, item) => {
    result[item.tabKey] = item.label
    return result
}, {})

const BENEFIT_MISSION_LABELS = DEFAULT_BENEFIT_MISSIONS.reduce((result, item) => {
    result[item.benefitKey] = item.label
    return result
}, {})

const EXPOSURE_MODE_META = {
    ON: {
        label: '자동 노출 허용',
        color: 'success',
        description: '노출을 허용합니다. 실제 표시 여부는 탭별 시즌 날짜 조건에 따라 자동으로 결정됩니다.',
    },
    OFF: {
        label: '강제 숨김',
        color: 'warning',
        description: '시즌 상태와 관계없이 앱 랭킹 탭에서 숨깁니다.',
    },
}

const normalizeRankingTabs = (rows = []) => {
    const normalizedMap = DEFAULT_RANKING_TABS.reduce((result, item) => {
        result[item.tabKey] = { ...item }
        return result
    }, {})

    rows.forEach((row) => {
        const tabKey = String(row?.tabKey || '').trim()

        if (!normalizedMap[tabKey]) return

        normalizedMap[tabKey] = {
            ...normalizedMap[tabKey],
            sortOrder: Number(row?.sortOrder || normalizedMap[tabKey].sortOrder),
            exposureMode: String(row?.exposureMode || normalizedMap[tabKey].exposureMode).toUpperCase() === 'AUTO'
                ? 'ON'
                : String(row?.exposureMode || normalizedMap[tabKey].exposureMode).toUpperCase(),
        }
    })

    return DEFAULT_RANKING_TABS.map((item) => normalizedMap[item.tabKey])
}

const parseDateOnlyValue = (value) => {
    if (!value) {
        return null
    }

    const dateValue = formatDate(value, { emptyFallback: '', invalidFallback: '' })
    const nextDate = new Date(`${dateValue}T00:00:00`)

    return Number.isNaN(nextDate.getTime()) ? null : nextDate
}

const formatDateOnlyValue = (value) => formatDate(value, {
    emptyFallback: null,
    invalidFallback: '',
    parseString: true,
})

const normalizeBenefitMissions = (rows = []) => {
    const normalizedMap = DEFAULT_BENEFIT_MISSIONS.reduce((result, item) => {
        result[item.benefitKey] = { ...item }
        return result
    }, {})

    rows.forEach((row) => {
        const benefitKey = String(row?.benefitKey || '').trim()

        if (!normalizedMap[benefitKey]) return

        normalizedMap[benefitKey] = {
            ...normalizedMap[benefitKey],
            exposureMode: String(row?.exposureMode || normalizedMap[benefitKey].exposureMode).toUpperCase(),
            startTime: parseDateOnlyValue(row?.startTime),
            endTime: parseDateOnlyValue(row?.endTime),
        }
    })

    return DEFAULT_BENEFIT_MISSIONS.map((item) => normalizedMap[item.benefitKey])
}

const getRankingTabDescription = (item) => {
    if (item.tabKey === 'department') {
        if (item.exposureMode === 'OFF') {
            return '부서별 대항전 탭을 시즌 상태와 관계없이 숨깁니다.'
        }

        return '자동 노출을 허용합니다. 실제 표시 여부는 날짜 정책을 따릅니다. 다음 대회 시작일 D-5부터 진행예정, 종료 후 다음 대회 D-5 전까지 종료 화면, 다음 대회가 없고 종료 후 7일이 지나면 숨김 처리됩니다.'
    }

    if (item.tabKey === 'country') {
        if (item.exposureMode === 'OFF') {
            return '국가대항전 탭을 시즌 상태와 관계없이 숨깁니다.'
        }

        return '현재 쿠팡 기업에서만 사용합니다. 자동 노출을 허용해도 실제 표시 여부는 날짜 정책을 따릅니다. 앱은 국가대항전 랭킹 화면으로 라우팅해야 합니다.'
    }

    return EXPOSURE_MODE_META[item.exposureMode]?.description
}

const isCountryRankingCompany = (companyCode) => String(companyCode || '').trim().toLowerCase() === 'coupang'

export default function AppCompany(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.companyId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchPeriod: 0, searchStatus: 0 }
    const listSearch = location.search || ''
    const draftKey = createEditAppCompanyDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [domainActionLoading, setDomainActionLoading] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [rankingTabs, setRankingTabs] = useState(DEFAULT_RANKING_TABS)
    const [isRankingTabConfigLoaded, setIsRankingTabConfigLoaded] = useState(false)
    const [benefitMissions, setBenefitMissions] = useState(DEFAULT_BENEFIT_MISSIONS)
    const [isBenefitMissionConfigLoaded, setIsBenefitMissionConfigLoaded] = useState(false)
    const [isBenefitMissionConfigEditable, setIsBenefitMissionConfigEditable] = useState(false)
    const [companyDomains, setCompanyDomains] = useState([])
    const [newDomain, setNewDomain] = useState('')
    const [editingDomainId, setEditingDomainId] = useState(null)
    const [editingDomainValue, setEditingDomainValue] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const domainActionInFlightRef = useRef(false)
    const initialDraftSnapshotRef = useRef('')
    const isInteractionLocked = saving || domainActionLoading

    const getCompanyDomains = useCallback(async () => {
        try {
            const domainRows = []
            let page = 1

            while (true) {
                const domainResponse = await API.post('/api/v1/admin/appCompany/getDomainList', {
                    page,
                    offset: COMPANY_DOMAIN_PAGE_SIZE,
                    searchText: '',
                    searchSelect: { searchType: id },
                })

                if (domainResponse.data.code === 200) {
                    const rows = domainResponse.data.data.rows || []
                    const totalCount = Number(domainResponse.data.data.count || 0)

                    domainRows.push(...rows)

                    if (rows.length === 0 || domainRows.length >= totalCount) {
                        setCompanyDomains(domainRows)
                        return true
                    }

                    page += 1
                    continue
                }

                if (domainResponse.data.code === 403) {
                    history.push('/login')
                    return false
                }

                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(domainResponse.data.message)
                return false
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인 목록을 불러오지 못했습니다.')
            return false
        }
    }, [history, id])

    const getRankingTabConfig = useCallback(async () => {
        setIsRankingTabConfigLoaded(false)

        try {
            const rankingTabResponse = await API.post('/api/v1/admin/appCompany/getRankingTabConfig', {
                companyId: id,
            })

            if (rankingTabResponse.data.code === 200) {
                setRankingTabs(normalizeRankingTabs(rankingTabResponse.data.data?.rows || []))
                setIsRankingTabConfigLoaded(true)
                return true
            }

            if (rankingTabResponse.data.code === 403) {
                history.push('/login')
                return false
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(rankingTabResponse.data.message)
            return false
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('랭킹 탭 설정을 불러오지 못했습니다.')
            return false
        }
    }, [history, id])

    const getBenefitMissionConfig = useCallback(async () => {
        setIsBenefitMissionConfigLoaded(false)
        setIsBenefitMissionConfigEditable(false)

        try {
            const benefitMissionResponse = await API.post('/api/v1/admin/appCompany/getBenefitMissionConfig', {
                companyId: id,
            })

            if (benefitMissionResponse.data.code === 200) {
                setBenefitMissions(normalizeBenefitMissions(benefitMissionResponse.data.data?.rows || []))
                setIsBenefitMissionConfigLoaded(true)
                setIsBenefitMissionConfigEditable(Number(benefitMissionResponse.data.data?.isEditable || 0) === 1)
                return true
            }

            if (benefitMissionResponse.data.code === 403) {
                history.push('/login')
                return false
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(benefitMissionResponse.data.message)
            return false
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('혜택 미션 설정을 불러오지 못했습니다.')
            return false
        }
    }, [history, id])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 기업 상세를 불러올 수 없습니다.'
                setParams(createDefaultParams())
                setCompanyDomains([])
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const [detailResponse] = await Promise.all([
                    API.post('/api/v1/admin/appCompany/getDetail', { companyId: id }),
                    getRankingTabConfig(),
                    getBenefitMissionConfig(),
                ])

                if (detailResponse.data.code === 200) {
                    const detailData = detailResponse.data.data || {}

                    if (!detailData.companyId) {
                        setParams(createDefaultParams())
                        setCompanyDomains([])
                        setDetailErrorMessage('기업 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const baseParams = {
                        ...detailData,
                        company_admin_info: detailData.company_admin_info || [],
                    }
                    const storedDraft = readAppCompanyDraft(draftKey)
                    const restoredDraft = applyAppCompanyDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppCompanyDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (detailResponse.data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = detailResponse.data.message || '기업 상세를 불러오지 못했습니다.'
                    setParams(createDefaultParams())
                    setCompanyDomains([])
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                    return
                }

                await getCompanyDomains()
            } catch (_error) {
                const errorMessage = '기업 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setCompanyDomains([])
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, getBenefitMissionConfig, getCompanyDomains, getRankingTabConfig, history, id])

    useEffect(() => {
        if (!draftReady || !params?.companyId) {
            return
        }

        // 기업 상세는 기본 정보 폼 draft만 sessionStorage에 보존하고 도메인/랭킹탭 상태는 제외한다.
        const nextDraft = createAppCompanyDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCompanyDraft(draftKey)
            return
        }

        saveAppCompanyDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        clearAppCompanyDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appCompanys',
            search: listSearch,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {}),
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: curPage
        }))
    }

    const handleUpdateField = (e) => {
        setParams((prevParams) => ({
            ...prevParams,
            [e.target.name]: e.target.name === 'isSignUpVisible' ? Number(e.target.value) : e.target.value
        }))
    }

    const handleUpdateRankingTab = (tabKey, field, value) => {
        setRankingTabs((prev) => prev.map((item) => {
            if (item.tabKey !== tabKey) return item

            return {
                ...item,
                [field]: value,
            }
        }))
    }

    const handleUpdateRankingTabSortOrder = (tabKey, nextSortOrder) => {
        const normalizedSortOrder = Number(nextSortOrder)

        setRankingTabs((prev) => {
            const currentItem = prev.find((item) => item.tabKey === tabKey)

            if (!currentItem || currentItem.sortOrder === normalizedSortOrder) {
                return prev
            }

            return prev.map((item) => {
                if (item.tabKey === tabKey) {
                    return {
                        ...item,
                        sortOrder: normalizedSortOrder,
                    }
                }

                if (item.sortOrder === normalizedSortOrder) {
                    return {
                        ...item,
                        sortOrder: currentItem.sortOrder,
                    }
                }

                return item
            })
        })
    }

    const handleUpdateBenefitMission = (benefitKey, field, value) => {
        setBenefitMissions((prev) => prev.map((item) => {
            if (item.benefitKey !== benefitKey) return item

            return {
                ...item,
                [field]: value,
            }
        }))
    }

    const handleClearBenefitMissionPeriod = (benefitKey) => {
        setBenefitMissions((prev) => prev.map((item) => {
            if (item.benefitKey !== benefitKey) return item

            return {
                ...item,
                startTime: null,
                endTime: null,
            }
        }))
    }

    const updateData = async () => {
        const nextCompanyName = String(params.companyName || '').trim()
        const nextCompanyNameEn = String(params.companyNameEn || '').trim()
        const nextMemo = String(params.memo || '').trim()

        setParams((prevParams) => ({
            ...prevParams,
            companyName: nextCompanyName,
            companyNameEn: nextCompanyNameEn,
            memo: nextMemo,
        }))

        if (!nextCompanyName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업명을 입력해주세요.')
            return true
        }

        if (params.planStartTime && !params.planEndTime) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('플랜종료일을 등록해주세요.')
            return true
        }
        if (params.planEndTime && !params.planStartTime) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('플랜시작일을 등록해주세요.')
            return true
        }

        const normalizedRankingTabs = rankingTabs
            .filter((item) => {
                if (item.tabKey !== 'country') return true
                return isCountryRankingCompany(params.companyCode)
            })
            .map((item) => ({
                tabKey: item.tabKey,
                sortOrder: Number(item.sortOrder),
                exposureMode: String(item.exposureMode || 'ON').toUpperCase(),
            }))

        for (const item of normalizedRankingTabs) {
            if (!Number.isInteger(item.sortOrder) || item.sortOrder < 1) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(`${RANKING_TAB_LABELS[item.tabKey] || '랭킹 탭'} 정렬 순서를 확인해주세요.`)
                return true
            }
        }

        const shouldSaveBenefitMissionConfig = isBenefitMissionConfigLoaded && isBenefitMissionConfigEditable
        const normalizedBenefitMissions = shouldSaveBenefitMissionConfig
            ? benefitMissions.map((item) => ({
                benefitKey: item.benefitKey,
                exposureMode: String(item.exposureMode || 'ON').toUpperCase(),
                startTime: formatDateOnlyValue(item.startTime),
                endTime: formatDateOnlyValue(item.endTime),
            }))
            : []

        for (const item of normalizedBenefitMissions) {
            if (!['ON', 'OFF'].includes(item.exposureMode)) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(`${BENEFIT_MISSION_LABELS[item.benefitKey] || '혜택 미션'} 노출 정책을 확인해주세요.`)
                return true
            }

            if (item.startTime === '' || item.endTime === '') {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(`${BENEFIT_MISSION_LABELS[item.benefitKey] || '혜택 미션'} 노출 기간을 확인해주세요.`)
                return true
            }

            if (item.startTime && item.endTime && item.startTime > item.endTime) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(`${BENEFIT_MISSION_LABELS[item.benefitKey] || '혜택 미션'} 노출 시작일은 종료일보다 늦을 수 없습니다.`)
                return true
            }
        }
        let companyInfoSaved = false

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appCompany/update', {
                companyId: params.companyId,
                companyName: nextCompanyName,
                companyNameEn: nextCompanyNameEn,
                planType: params.planType,
                limitUsers: params.limitUsers,
                planStartTime: params.planStartTime,
                planEndTime: params.planEndTime,
                memo: nextMemo,
                isSignUpVisible: params.isSignUpVisible
            })

            if (data.code === 200) {
                companyInfoSaved = true

                if (!isRankingTabConfigLoaded) {
                    // 저장 후에도 운영자가 보던 기업 목록 조건으로 그대로 복귀시킨다.
                    moveToList({
                        severity: 'success',
                        message: `${data.message} 랭킹 탭 설정은 불러오지 못해 변경하지 않았습니다.`,
                    })
                    return
                }

                const rankingTabResponse = await API.post('/api/v1/admin/appCompany/updateRankingTabConfig', {
                    companyId: params.companyId || id,
                    rows: normalizedRankingTabs,
                })

                if (rankingTabResponse.data.code === 403) {
                    history.push('/login')
                    return
                }

                if (rankingTabResponse.data.code !== 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(`기업 정보는 저장되었지만 랭킹 탭 설정 저장에 실패했습니다. ${rankingTabResponse.data.message}`)
                    return
                }

                if (!isBenefitMissionConfigLoaded) {
                    // 저장 후에도 운영자가 보던 기업 목록 조건으로 그대로 복귀시킨다.
                    moveToList({
                        severity: 'success',
                        message: `${data.message} 혜택 미션 설정은 불러오지 못해 변경하지 않았습니다.`,
                    })
                    return
                }

                if (shouldSaveBenefitMissionConfig) {
                    const benefitMissionResponse = await API.post('/api/v1/admin/appCompany/updateBenefitMissionConfig', {
                        companyId: params.companyId || id,
                        rows: normalizedBenefitMissions,
                    })

                    if (benefitMissionResponse.data.code === 403) {
                        history.push('/login')
                        return
                    }

                    if (benefitMissionResponse.data.code !== 200) {
                        setOpenSnackbar(true)
                        setSeveritySnackbar('error')
                        setMessageSnackbar(`기업 정보와 랭킹 탭 설정은 저장되었지만 혜택 미션 설정 저장에 실패했습니다. ${benefitMissionResponse.data.message}`)
                        return
                    }
                }

                // 저장 후에도 운영자가 보던 기업 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(companyInfoSaved
                ? '기업 정보는 저장되었지만 보조 설정 저장에 실패했습니다.'
                : '기업 정보를 저장하지 못했습니다.'
            )
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        updateData()
    }

    const handleCreateDomain = async () => {
        const nextDomain = String(newDomain || '').trim()

        if (domainActionInFlightRef.current) {
            return
        }

        if (!nextDomain || nextDomain.indexOf('.') === -1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인 형식이 잘못되었습니다.')
            return
        }

        domainActionInFlightRef.current = true
        setDomainActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/createDomain', {
                companyId: id,
                domain: nextDomain,
            })

            if (data.code === 200) {
                setNewDomain('')
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('도메인이 등록되었습니다.')
                await getCompanyDomains()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인을 등록하지 못했습니다.')
        } finally {
            domainActionInFlightRef.current = false
            setDomainActionLoading(false)
        }
    }

    const handleDeleteDomain = async (companyDomainId) => {
        if (domainActionInFlightRef.current) {
            return
        }

        domainActionInFlightRef.current = true
        setDomainActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/deleteDomain', { companyDomainId })

            if (data.code === 200) {
                if (editingDomainId === companyDomainId) {
                    setEditingDomainId(null)
                    setEditingDomainValue('')
                }
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('도메인이 삭제되었습니다.')
                await getCompanyDomains()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인을 삭제하지 못했습니다.')
        } finally {
            domainActionInFlightRef.current = false
            setDomainActionLoading(false)
        }
    }

    const handleStartEditDomain = (companyDomainId, domain) => {
        setEditingDomainId(companyDomainId)
        setEditingDomainValue(domain || '')
    }

    const handleCancelEditDomain = () => {
        setEditingDomainId(null)
        setEditingDomainValue('')
    }

    const handleUpdateDomain = async (companyDomainId) => {
        const nextDomainValue = String(editingDomainValue || '').trim()

        if (domainActionInFlightRef.current) {
            return
        }

        if (!nextDomainValue || nextDomainValue.indexOf('.') === -1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인 형식이 잘못되었습니다.')
            return
        }

        domainActionInFlightRef.current = true
        setDomainActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appCompany/updateDomain', {
                companyDomainId,
                companyId: id,
                domain: nextDomainValue,
            })

            if (data.code === 200) {
                setEditingDomainId(null)
                setEditingDomainValue('')
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('도메인이 수정되었습니다.')
                await getCompanyDomains()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인을 수정하지 못했습니다.')
        } finally {
            domainActionInFlightRef.current = false
            setDomainActionLoading(false)
        }
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="기업 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.companyId) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 상세</Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="기업코드"
                                        id="companyCode"
                                        name="companyCode"
                                        value={params.companyCode || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="기업명"
                                        id="companyName"
                                        name="companyName"
                                        value={params.companyName || ''}
                                        onChange={handleUpdateField}
                                        disabled={isInteractionLocked}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="기업명 영문"
                                        id="companyNameEn"
                                        name="companyNameEn"
                                        value={params.companyNameEn || ''}
                                        onChange={handleUpdateField}
                                        disabled={isInteractionLocked}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="계정이메일"
                                        id="email"
                                        name="email"
                                        value={params.email || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        select
                                        label="회원가입 노출여부"
                                        id="isSignUpVisible"
                                        name="isSignUpVisible"
                                        value={params.isSignUpVisible ?? 1}
                                        onChange={handleUpdateField}
                                        disabled={isInteractionLocked}
                                        size="small"
                                    >
                                        <MenuItem value={1}>노출</MenuItem>
                                        <MenuItem value={0}>미노출</MenuItem>
                                    </TextField>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="담당자 이름"
                                        id="adminName"
                                        name="adminName"
                                        value={!params.company_admin_info[0]?.adminName ? '미입력' : params.company_admin_info[0].adminName }
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="담당자 이메일"
                                        id="adminEmail"
                                        name="adminEmail"
                                        value={!params.company_admin_info[0]?.adminEmail ? '미입력' : params.company_admin_info[0].adminEmail}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="담당자 연락처"
                                        id="adminPhone"
                                        name="adminPhone"
                                        value={!params.company_admin_info[0]?.adminPhone ? '미입력' : params.company_admin_info[0].adminPhone}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="담당자 부서"
                                        id="adminDepartment"
                                        name="adminDepartment"
                                        value={!params.company_admin_info[0]?.adminDepartment ? '미입력' : params.company_admin_info[0].adminDepartment}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="담당자 직책"
                                        id="adminPosition"
                                        name="adminPosition"
                                        value={!params.company_admin_info[0]?.adminPosition ? '미입력' : params.company_admin_info[0].adminPosition}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid>
                            {/* <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="플랜타입"
                                        id="planType"
                                        name="planType"
                                        value={params.planType === 1 ? '기간형' : '구독형'}
                                        onChange={handleUpdateField}
                                        size="small"
                                        InputProps={{ readOnly: true}}
                                        variant="filled"
                                    />
                                </FormControl>
                            </Grid> */}
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="구성원용량"
                                        type="number"
                                        id="limitUsers"
                                        name="limitUsers"
                                        value={params.limitUsers || 0}
                                        onChange={handleUpdateField}
                                        disabled={isInteractionLocked}
                                        size="small"
                                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="플랜시작일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={params.planStartTime}
                                            disabled={isInteractionLocked}
                                            onChange={d => setParams({
                                                ...params,
                                                planStartTime: d
                                            })}
                                            renderInput={(params) => <TextField {...params} size="small"/>}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={6} sm={6}>
                                <FormControl fullWidth>
                                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                        <DesktopDatePicker
                                            label="플랜종료일"
                                            inputFormat="yyyy-MM-dd"
                                            mask={"____-__-__"}
                                            value={params.planEndTime}
                                            disabled={isInteractionLocked}
                                            minDate={new Date(params.planStartTime)}
                                            onChange={d => setParams({
                                                ...params,
                                                planEndTime: d
                                            })}
                                            renderInput={(params) => <TextField {...params} size="small" />}
                                        />
                                    </LocalizationProvider>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="메모"
                                        multiline
                                        rows={7}
                                        name="memo"
                                        value={params.memo || ''}
                                        onChange={handleUpdateField}
                                        disabled={isInteractionLocked}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        랭킹 탭 설정
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={isRankingTabConfigLoaded ? 'text.secondary' : 'warning.main'}
                                        sx={{ mb: 2 }}
                                    >
                                        {isRankingTabConfigLoaded
                                            ? '만보기 탭은 앱에서 고정으로 처리됩니다. 강제 숨김을 선택하면 시즌 상태와 관계없이 숨겨지고, 자동 노출 허용을 선택해도 부서별 대항전과 국가대항전은 시즌 날짜 조건이 맞을 때만 표시됩니다.'
                                            : '랭킹 탭 설정을 불러오지 못했습니다. 현재 기업 정보만 수정 가능하며, 랭킹 탭 설정 저장은 비활성화됩니다.'}
                                    </Typography>
                                    {rankingTabs
                                        .slice()
                                        .sort((a, b) => a.sortOrder - b.sortOrder)
                                        .map((item) => {
                                        const isCountryTab = item.tabKey === 'country'
                                        const isCountryCompany = isCountryRankingCompany(params.companyCode)
                                        const isCountryControlDisabled = isCountryTab && !isCountryCompany

                                        return (
                                        <Box
                                            key={item.tabKey}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2,
                                                p: 2,
                                                mb: 1.5,
                                                backgroundColor: 'grey.50',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {getRankingTabDescription(item)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        label={`${item.sortOrder}번째 노출`}
                                                    />
                                                    {isCountryTab && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label="쿠팡 전용"
                                                        />
                                                    )}
                                                    <Chip
                                                        size="small"
                                                        color={EXPOSURE_MODE_META[item.exposureMode]?.color || 'default'}
                                                        label={EXPOSURE_MODE_META[item.exposureMode]?.label || item.exposureMode}
                                                    />
                                                </Box>
                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            select
                                                            label="노출 정책"
                                                            value={item.exposureMode}
                                                            size="small"
                                                            disabled={isInteractionLocked || !isRankingTabConfigLoaded || isCountryControlDisabled}
                                                            onChange={(e) => handleUpdateRankingTab(item.tabKey, 'exposureMode', e.target.value)}
                                                        >
                                                            <MenuItem value="ON">자동 노출 허용</MenuItem>
                                                            <MenuItem value="OFF">강제 숨김</MenuItem>
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} sm={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <TextField
                                                            select
                                                            label="노출 순서"
                                                            value={item.sortOrder}
                                                            size="small"
                                                            disabled={isInteractionLocked || !isRankingTabConfigLoaded || isCountryControlDisabled}
                                                            onChange={(e) => handleUpdateRankingTabSortOrder(item.tabKey, e.target.value)}
                                                        >
                                                            {DEFAULT_RANKING_TABS.map((option) => (
                                                                <MenuItem key={option.sortOrder} value={option.sortOrder}>
                                                                    {option.sortOrder}번째
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                            {isCountryTab && !isCountryCompany && (
                                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1.5 }}>
                                                    현재 회사에서는 국가대항전 탭을 실제로 사용하지 않으므로 설정을 변경할 수 없습니다. 쿠팡 회사에서만 앱 노출 대상입니다.
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                                노출 순서를 바꾸면 다른 탭과 자동으로 자리가 바뀝니다.
                                            </Typography>
                                        </Box>
                                    )})}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        혜택 미션 노출 설정
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={isBenefitMissionConfigLoaded && isBenefitMissionConfigEditable ? 'text.secondary' : 'warning.main'}
                                        sx={{ mb: 2 }}
                                    >
                                        {!isBenefitMissionConfigLoaded
                                            ? '혜택 미션 설정을 불러오지 못했습니다. 현재 기업 정보만 수정 가능하며, 혜택 미션 설정 저장은 비활성화됩니다.'
                                            : isBenefitMissionConfigEditable
                                                ? '쿠팡 기업 전용 설정입니다. 노출 정책이 ON이어도 시작일과 종료일을 벗어나면 앱 혜택 영역에 표시되지 않습니다.'
                                                : '혜택 미션 노출 설정은 쿠팡 기업에서만 수정할 수 있습니다.'}
                                    </Typography>
                                    {benefitMissions.map((item) => {
                                        const isBenefitMissionControlDisabled = isInteractionLocked || !isBenefitMissionConfigLoaded || !isBenefitMissionConfigEditable

                                        return (
                                            <Box
                                                key={item.benefitKey}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    p: 2,
                                                    mb: 1.5,
                                                    backgroundColor: 'grey.50',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {item.label}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.exposureMode === 'OFF'
                                                                ? '앱 혜택 영역에서 숨깁니다.'
                                                                : '설정 기간 안에서 앱 혜택 영역에 노출합니다.'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label="쿠팡 전용"
                                                        />
                                                        <Chip
                                                            size="small"
                                                            color={EXPOSURE_MODE_META[item.exposureMode]?.color || 'default'}
                                                            label={EXPOSURE_MODE_META[item.exposureMode]?.label || item.exposureMode}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={12} md={4}>
                                                        <FormControl fullWidth>
                                                            <TextField
                                                                select
                                                                label="노출 정책"
                                                                value={item.exposureMode}
                                                                size="small"
                                                                disabled={isBenefitMissionControlDisabled}
                                                                onChange={(e) => handleUpdateBenefitMission(item.benefitKey, 'exposureMode', e.target.value)}
                                                            >
                                                                <MenuItem value="ON">노출</MenuItem>
                                                                <MenuItem value="OFF">미노출</MenuItem>
                                                            </TextField>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <FormControl fullWidth>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                                                <DesktopDatePicker
                                                                    label="노출 시작일"
                                                                    inputFormat="yyyy-MM-dd"
                                                                    mask={"____-__-__"}
                                                                    value={item.startTime}
                                                                    disabled={isBenefitMissionControlDisabled}
                                                                    onChange={(d) => handleUpdateBenefitMission(item.benefitKey, 'startTime', d)}
                                                                    renderInput={(params) => <TextField {...params} size="small" />}
                                                                />
                                                            </LocalizationProvider>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} md={3}>
                                                        <FormControl fullWidth>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                                                <DesktopDatePicker
                                                                    label="노출 종료일"
                                                                    inputFormat="yyyy-MM-dd"
                                                                    mask={"____-__-__"}
                                                                    value={item.endTime}
                                                                    disabled={isBenefitMissionControlDisabled}
                                                                    minDate={item.startTime || undefined}
                                                                    onChange={(d) => handleUpdateBenefitMission(item.benefitKey, 'endTime', d)}
                                                                    renderInput={(params) => <TextField {...params} size="small" />}
                                                                />
                                                            </LocalizationProvider>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} sm={12} md={2}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            onClick={() => handleClearBenefitMissionPeriod(item.benefitKey)}
                                                            disabled={isBenefitMissionControlDisabled}
                                                            sx={{ height: 40 }}
                                                        >
                                                            기간 해제
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        도메인 관리
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="도메인 @다음부터 입력해주세요"
                                            value={newDomain}
                                            onChange={(e) => setNewDomain(e.target.value)}
                                            disabled={isInteractionLocked}
                                            size="small"
                                        />
                                        <Button variant="contained" onClick={handleCreateDomain} disabled={isInteractionLocked}>
                                            추가
                                        </Button>
                                    </Box>
                                    {companyDomains.length === 0 ? (
                                        <TextField
                                            fullWidth
                                            value="등록된 도메인이 없습니다."
                                            size="small"
                                            InputProps={{ readOnly: true }}
                                            variant="filled"
                                        />
                                    ) : (
                                        companyDomains.map((item) => (
                                            <Box key={item.companyDomainId} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    label="도메인"
                                                    value={editingDomainId === item.companyDomainId ? editingDomainValue : (item.domain || '')}
                                                    size="small"
                                                    onChange={(e) => setEditingDomainValue(e.target.value)}
                                                    disabled={isInteractionLocked}
                                                    InputProps={{ readOnly: isInteractionLocked || editingDomainId !== item.companyDomainId }}
                                                    variant={editingDomainId === item.companyDomainId ? 'outlined' : 'filled'}
                                                />
                                                <Box sx={{ display: 'flex', gap: 1, opacity: isInteractionLocked ? 0.5 : 1, pointerEvents: isInteractionLocked ? 'none' : 'auto' }}>
                                                {editingDomainId === item.companyDomainId ? (
                                                    <>
                                                        <Button variant="contained" onClick={() => handleUpdateDomain(item.companyDomainId)} disabled={isInteractionLocked}>
                                                            저장
                                                        </Button>
                                                        <Button variant="outlined" onClick={handleCancelEditDomain} disabled={isInteractionLocked}>
                                                            취소
                                                        </Button>
                                                        <DeleteAlertDialog handleDelete={handleDeleteDomain} id={item.companyDomainId} disabled={isInteractionLocked}/>
                                                    </>
                                                ) : (
                                                    <ListActionGroup sx={{ minHeight: 40 }}>
                                                        <Tooltip title="Edit">
                                                            <Box
                                                                component="button"
                                                                type="button"
                                                                onClick={() => handleStartEditDomain(item.companyDomainId, item.domain)}
                                                                aria-label="Edit"
                                                                disabled={isInteractionLocked}
                                                                sx={{
                                                                    ...LIST_ACTION_EDIT_BUTTON_SX,
                                                                    cursor: isInteractionLocked ? 'default' : 'pointer',
                                                                    opacity: isInteractionLocked ? 0.5 : 1,
                                                                    color: isInteractionLocked ? 'rgba(100, 116, 139, 0.42)' : LIST_ACTION_EDIT_BUTTON_SX.color,
                                                                }}
                                                            >
                                                                <ModeEditIcon />
                                                            </Box>
                                                        </Tooltip>
                                                        <DeleteAlertDialog handleDelete={handleDeleteDomain} id={item.companyDomainId} disabled={isInteractionLocked}/>
                                                    </ListActionGroup>
                                                )}
                                                </Box>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        <BottomButtonGroup path="/appCompanys" search={listSearch} visible={true} searchText={searchText} searchSelect={searchSelect} curPage={curPage} submitDisabled={isInteractionLocked} submitLabel={saving ? '저장 중...' : '저장'} onBackClick={() => clearAppCompanyDraft(draftKey)} />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
        </Grid>
    )
}
